import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { Course, Product } from '@mira-avant/mtlibs';
import { CourseCreatePayload, CourseUpdatePayload } from '@mira-avant/mtlibs';
import { LanguageService } from './language.service';
import { UnitService } from './unit.service';
import { ApiService } from './api.service';
import { NavigationService } from './navigation.service';

@Injectable({
	providedIn: 'root',
})
export class CourseService {
	private readonly BASE_PATH = 'courses';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public courses$: BehaviorSubject<Course[]> = new BehaviorSubject<Course[]>([]);

	constructor(
		private api: ApiService,
		private languageService: LanguageService,
		private unitService: UnitService,
		private nav: NavigationService
	) {}

	public async onClientChange$(clientUuid: string | undefined): Promise<void> {
		this.courses$.next([]);

		if (!clientUuid) {
			return;
		}

		await this.getByClient$(clientUuid);
	}

	public setActiveText(course: Course): void {
		const currentLang: string = this.languageService.spokenLanguage$.value;
		const activeText = course.course_text!.find(courseText => courseText.default_lang_id === currentLang);
		course.active_text = activeText || course.course_text![0];
	}

	public sortCourses(courses: Course[]): Course[] {
		return courses.sort((a: Course, b: Course) => {
			if (a.display_order !== b.display_order) {
				return a.display_order - b.display_order;
			}

			if (!a.active_text || !b.active_text) {
				return 0;
			}

			return a.active_text.name.localeCompare(b.active_text.name);
		});
	}

	public processCourse(course: Course): Course {
		this.setActiveText(course);
		if (course.unit && Array.isArray(course.unit)) {
			course.unit = this.unitService.processUnits(course.unit);
		}
		return course;
	}

	public processCourses(courses: Course[]): Course[] {
		return this.sortCourses(courses.map(course => this.processCourse(course)));
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async get$(uuid: string, expandChildren: boolean = false, expandAll: boolean = false): Promise<Course> {
		return this.withLoading(async () => {
			const params = {
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const course = await firstValueFrom(
				this.api.get<Course>(`${this.BASE_PATH}/get-by-uuid/${uuid}`, { params })
			);
			return this.processCourse(course);
		});
	}

	public async getProduct$(uuid: string): Promise<Product> {
		return this.withLoading(async () => {
			const product = await firstValueFrom(this.api.get<Product>(`${this.BASE_PATH}/get-product/${uuid}`));
			return product;
		});
	}

	public async create$(courseData: CourseCreatePayload): Promise<Course> {
		return this.withLoading(async () => {
			const newCourse = await firstValueFrom(this.api.post<Course>(this.BASE_PATH, courseData));
			const processedCourse = this.processCourse(newCourse);
			const currentCourses = this.courses$.value;
			const updatedCourses = this.processCourses([...currentCourses, processedCourse]);
			this.courses$.next(updatedCourses);
			return processedCourse;
		});
	}

	public async update$(uuid: string, courseData: CourseUpdatePayload): Promise<Course> {
		return this.withLoading(async () => {
			const updatedCourse = await firstValueFrom(this.api.put<Course>(`${this.BASE_PATH}/${uuid}`, courseData));
			const processedCourse = this.processCourse(updatedCourse);
			const currentCourses = this.courses$.value;
			const updatedCourses = this.processCourses(
				currentCourses.map(course => (course.uuid === uuid ? processedCourse : course))
			);
			this.courses$.next(updatedCourses);
			return processedCourse;
		});
	}

	public async delete$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete<void>(`${this.BASE_PATH}/${uuid}`));
			const currentCourses = this.courses$.value;
			const updatedCourses = currentCourses.filter(course => course.uuid !== uuid);
			this.courses$.next(updatedCourses);
		});
	}

	public async getByClient$(
		clientUuid: string,
		expandChildren: boolean = false,
		expandAll: boolean = false
	): Promise<Course[]> {
		return this.withLoading(async () => {
			const params = {
				clientUuid,
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const courses = await firstValueFrom(this.api.get<Course[]>(`${this.BASE_PATH}/client`, { params }));
			const processedCourses = this.processCourses(courses);
			this.courses$.next(processedCourses);
			return processedCourses;
		});
	}

	public async getByCname$(
		cname: string,
		expandChildren: boolean = false,
		expandAll: boolean = false
	): Promise<Course> {
		return this.withLoading(async () => {
			const params = {
				cname,
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const course = await firstValueFrom(this.api.get<Course>(`${this.BASE_PATH}/cname`, { params }));
			return this.processCourse(course);
		});
	}

	public async duplicate$(uuid: string, clientParentUuid: string): Promise<Course> {
		return this.withLoading(async () => {
			const duplicatedCourse = await firstValueFrom(
				this.api.post<Course>(`${this.BASE_PATH}/duplicate/${uuid}/${clientParentUuid}`, {})
			);
			const processedCourse = this.processCourse(duplicatedCourse);
			const currentCourses = this.courses$.value;
			const updatedCourses = this.processCourses([...currentCourses, processedCourse]);
			this.courses$.next(updatedCourses);
			return processedCourse;
		});
	}

	public async duplicateBulk$(uuids: string[], clientParentUuid: string): Promise<Course[]> {
		return this.withLoading(async () => {
			const duplicatedCourses = await firstValueFrom(
				this.api.post<Course[]>(`${this.BASE_PATH}/duplicate-bulk/${clientParentUuid}`, { uuids })
			);
			const processedCourses = this.processCourses(duplicatedCourses);
			const currentCourses = this.courses$.value;
			const newUuidSet = new Set(processedCourses.map(course => course.uuid));
			const filteredCurrent = currentCourses.filter(course => !newUuidSet.has(course.uuid));
			const updatedCourses = this.processCourses([...processedCourses, ...filteredCurrent]);
			this.courses$.next(updatedCourses);
			return processedCourses;
		});
	}

	public async moveUp$(uuid: string, clientUuid: string): Promise<Course[]> {
		return this.withLoading(async () => {
			const updatedCourses = await firstValueFrom(
				this.api.post<Course[]>(`${this.BASE_PATH}/move-up/${uuid}/${clientUuid}`, {})
			);
			const processedCourses = this.processCourses(updatedCourses);
			this.courses$.next(processedCourses);
			return processedCourses;
		});
	}

	public async moveDown$(uuid: string, clientUuid: string): Promise<Course[]> {
		return this.withLoading(async () => {
			const updatedCourses = await firstValueFrom(
				this.api.post<Course[]>(`${this.BASE_PATH}/move-down/${uuid}/${clientUuid}`, {})
			);
			const processedCourses = this.processCourses(updatedCourses);
			this.courses$.next(processedCourses);
			return processedCourses;
		});
	}

	public async getDefaultLanguageForLessonId$(lessonId: string): Promise<string> {
		const courses: Course[] = await firstValueFrom(
			this.courses$.pipe(filter((courses: Course[]) => courses.length > 0))
		);

		for (const course of courses) {
			if (course.unit && Array.isArray(course.unit)) {
				for (const unit of course.unit) {
					if (unit.lesson && unit.lesson.some(lesson => lesson.uuid === lessonId)) {
						return course.default_lang_id;
					}
				}
			}
		}

		return this.languageService.spokenLanguage$.value;
	}

	/**
	 * Get course by URL parameters.
	 *
	 * @public
	 * @async
	 * @returns {Promise<Course | undefined>}
	 */
	public async getCourseByUrl(): Promise<Course | undefined> {
		return firstValueFrom(this.nav.matrixParams$).then(async (matrixParams: { [key: string]: string }) => {
			const courseUuid = matrixParams['courseUuid'];

			if (!courseUuid) {
				return undefined;
			}

			try {
				const course = await this.get$(courseUuid, false, false);
				return course;
			} catch (error) {
				console.error('Error fetching course by URL:', error);
				return undefined;
			}
		});
	}
}
